import type { ComponentRef, Ref } from 'react'
import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { InfoIcon } from 'lucide-react'

import { cn } from '../../lib/utils'
import { Button } from './button'

const Popover = ({ modal = true, ...props }: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>) => (
    <PopoverPrimitive.Root modal={modal} {...props} />
)
const PopoverAnchor = PopoverPrimitive.Anchor
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverClose = PopoverPrimitive.Close

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    ref?: Ref<ComponentRef<typeof PopoverPrimitive.Content>>
}

function PopoverContent({ className, align = 'center', sideOffset = 4, ref, ...props }: PopoverContentProps) {
    return (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
                ref={ref}
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    'z-50 rounded-md border border-gray-200 bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 [&_a]:text-white',
                    className,
                )}
                {...props}
            />
        </PopoverPrimitive.Portal>
    )
}

interface TooltipStyledPopoverProps {
    popoverTrigger?: React.ReactNode
    children: React.ReactNode
    alt?: string
}

function TooltipStyledPopover({
    popoverTrigger,
    children,
    alt,
}: TooltipStyledPopoverProps): React.JSX.Element {
    const [open, setOpen] = React.useState(false)

    const handleToggle = React.useCallback((e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setOpen((prev) => !prev)
    }, [])

    return (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen} modal>
            <PopoverPrimitive.Trigger
                className="bg-transparent touch-manipulation"
                onClick={handleToggle}
                onTouchEnd={handleToggle}
                type="button"
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={alt}
                asChild={!popoverTrigger}
            >
                {popoverTrigger ?? (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="ml-1 h-4 w-4 p-0 bg-transparent hover:bg-transparent"
                    >
                        <InfoIcon className="w-4 h-4 text-black" />
                    </Button>
                )}
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                    sideOffset={5}
                    collisionPadding={16}
                    onPointerDownOutside={(e) => {
                        e.preventDefault()
                        setOpen(false)
                    }}
                    onEscapeKeyDown={() => setOpen(false)}
                    onInteractOutside={(e) => {
                        e.preventDefault()
                        setOpen(false)
                    }}
                    className={cn(
                        'z-50 flex max-w-[300px] self-center justify-center rounded-lg p-2 bg-black text-white text-sm leading-4 select-none',
                        'shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px]',
                        'animate-in fade-in-0 zoom-in-95 duration-100',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    )}
                >
                    {children}
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
    )
}

export {
    Popover,
    PopoverAnchor,
    PopoverClose,
    PopoverContent,
    PopoverTrigger,
    TooltipStyledPopover,
}
